import { useEffect, useState } from "react";
import { adminApi } from "../../services/adminApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { DataTable } from "../../components/common/DataTable";

/**
 * Accounts that signed up, looked once, and never came back (#480).
 *
 * ── WHY THIS SITS BESIDE FEATURE ADOPTION AND NOT INSIDE IT ──
 *
 * Feature Adoption measures SCREENS: it can say somebody abandoned the
 * onboarding form, and since #379's Option C it can say whether they
 * skipped deliberately or left. Every one of those people still has an
 * account.
 *
 * This asks whether the account went on to be used at all, which is a
 * more expensive failure and a different question. Keeping them as two
 * pages stops one being read as the other.
 *
 * ── THE NUMBERS ARE SHOWN AS COUNTS AND AS SHARE ──
 *
 * "14 one-and-done" is unreadable without the size of the band it came
 * from, and a share alone hides that a band had four accounts in it.
 * Both, always, for the reason the feature-usage page learned: a
 * percentage over a tiny denominator looks like a finding.
 */
export function DormantAccountsPage() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    adminApi.dormantAccounts()
      .then((r) => { setData(r); setFailed(false); })
      // null stays null: "we could not load it" is not "there are no
      // dormant accounts", and the second is a claim somebody would act
      // on.
      .catch(() => setFailed(true));
  }, []);

  if (failed) {
    return (
      <div>
        <h1>Dormant Accounts</h1>
        <Card><p style={{ margin: 0 }}>Couldn&apos;t load this just now.</p></Card>
      </div>
    );
  }
  if (!data) return <Spinner />;

  const bands = data.bands || [];
  const share = (n, total) => (total > 0 ? `${Math.round((n / total) * 100)}%` : "—");

  return (
    <div>
      <h1>Dormant Accounts</h1>

      <Card className="section-spacing">
        <p style={{ margin: 0 }}>
          Accounts grouped by how long ago they <strong>registered</strong>. An account
          appears in the oldest band it qualifies for and nowhere else, so the bands do
          not overlap and the shares below add up.
        </p>
        <p style={{ marginBottom: 0 }}>
          {/* SAID ON THE PAGE, not only in the API docstring. The person
              reading a table never sees a docstring, and "logged in
              once" is a claim this data can only nearly support. */}
          <strong>One row per session, not provably per login.</strong> A second device
          or a re-authentication creates another session. For a one-and-done account the
          distinction is almost certainly immaterial — one device, once — but it is what
          the figure counts. Dormant means no session in the
          last {data.dormant_after_days} days.
        </p>
      </Card>

      {bands.length === 0 ? (
        <Card>
          {/* An empty report and a broken one are different, and this is
              the empty one: no account is old enough to ask about yet. */}
          <p style={{ margin: 0 }}>
            No account is more than 30 days old yet, so there is nothing to ask about.
          </p>
        </Card>
      ) : (
        <Card title="By account age and plan">
          <DataTable
            rows={bands.map((b, i) => ({ ...b, id: `${b.age_band}-${b.subscription_tier}-${i}` }))}
            loading={false}
            rowKey="id"
            columns={[
              { key: "age_band", header: "Registered over", render: (r) => `${r.age_band} days ago` },
              { key: "subscription_tier", header: "Plan" },
              { key: "accounts", header: "Accounts" },
              /* NEVER SIGNED IN is first and kept apart from
                 one-and-done. It is a failure at a different step --
                 the account exists and the person never got in -- and
                 merging the two would hide it. */
              { key: "never_signed_in", header: "Never signed in",
                render: (r) => `${r.never_signed_in} (${share(r.never_signed_in, r.accounts)})` },
              { key: "one_and_done", header: "One and done",
                render: (r) => `${r.one_and_done} (${share(r.one_and_done, r.accounts)})` },
              { key: "dormant", header: "Dormant",
                render: (r) => `${r.dormant} (${share(r.dormant, r.accounts)})` },
              /* THE CONTROL. Without it the other three are numbers
                 with nothing to compare them to. */
              { key: "active", header: "Still active",
                render: (r) => `${r.active} (${share(r.active, r.accounts)})` },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
